import React, { useState } from 'react'
import { primaryDates, getDaysUntilPrimary, parseLocalDate } from '../lib/primaryDates'
import GMPTimeline from './analytics/GMPTimeline'

function PrimaryCalendar({ clients = [] }) {
  const [viewMode, setViewMode] = useState('timeline')
  const [showOnlyClientStates, setShowOnlyClientStates] = useState(false)
  const [selectedState, setSelectedState] = useState(null)
  
  // Get unique states from clients
  const clientStates = [...new Set(clients.map(c => c.state?.toUpperCase()).filter(Boolean))]
  
  // Filter primaries if toggle is on
  const filteredPrimaries = showOnlyClientStates 
    ? primaryDates.filter(p => clientStates.includes(p.state))
    : primaryDates
  
  // Sort by date (local calendar day — matches getDaysUntilPrimary / MA month headings)
  const sortedPrimaries = [...filteredPrimaries].sort(
    (a, b) => parseLocalDate(a.primary) - parseLocalDate(b.primary)
  )

  // Group by month for timeline view
  const groupedByMonth = {}
  sortedPrimaries.forEach(p => {
    const date = parseLocalDate(p.primary)
    const monthKey = date.toLocaleString('default', { month: 'long', year: 'numeric' })
    if (!groupedByMonth[monthKey]) groupedByMonth[monthKey] = []
    groupedByMonth[monthKey].push(p)
  })

  const getDaysLabel = (stateAbbr) => {
    const days = getDaysUntilPrimary(stateAbbr)
    if (days === null) return ''
    if (days < 0) return 'Passed'
    if (days === 0) return 'Today!'
    if (days === 1) return '1 day'
    return `${days} days`
  }

  const getUrgencyColor = (stateAbbr) => {
    const days = getDaysUntilPrimary(stateAbbr)
    if (days === null || days < 0) return '#9ca3af'
    if (days <= 14) return '#ef4444'
    if (days <= 30) return '#f59e0b'
    if (days <= 60) return '#eab308'
    return '#22c55e'
  }

  const hasClients = (stateAbbr) => clientStates.includes(stateAbbr)
  
  const getClientCount = (stateAbbr) => {
    return clients.filter(c => c.state?.toUpperCase() === stateAbbr).length
  }

  const getClientsForState = (stateAbbr) => {
    return clients.filter(c => c.state?.toUpperCase() === stateAbbr)
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      timeZone: 'UTC'
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const getSelectedStateData = () => {
    return primaryDates.find(p => p.state === selectedState)
  }

  const getStateBudgetTotal = (stateAbbr) => {
    return getClientsForState(stateAbbr).reduce((sum, c) => 
      sum + (c.linear_budget || 0) + (c.streaming_budget || 0), 0
    )
  }

  // Modal component
  const StateModal = () => {
    if (!selectedState) return null
    
    const stateData = getSelectedStateData()
    const stateClients = getClientsForState(selectedState)
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15,23,42,0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => setSelectedState(null)}
      >
        <div
          style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--color-border)',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-xl)'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                {stateData?.name}
              </h2>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Primary: {formatDate(stateData?.primary)}
                {stateData?.runoff && ` • Runoff: ${formatDate(stateData.runoff)}`}
              </div>
              <div style={{ 
                marginTop: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: getUrgencyColor(selectedState)
              }}>
                {getDaysLabel(selectedState)}
              </div>
            </div>
            <button
              onClick={() => setSelectedState(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '0',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>

          {/* Modal Body */}
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {stateClients.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                No clients in {stateData?.name}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Client</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Race</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>AE</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {stateClients.map((client, idx) => (
                    <tr 
                      key={client.id}
                      style={{ 
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb'
                      }}
                    >
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {client.client_name}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                        {client.race || '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: client.status === 'Active' ? '#d1fae5' : client.status === 'Done' ? '#e5e7eb' : '#fef3c7',
                          color: client.status === 'Active' ? '#065f46' : client.status === 'Done' ? '#374151' : '#92400e'
                        }}>
                          {client.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>
                        {client.account_executive || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontFamily: 'monospace', color: '#374151', textAlign: 'right' }}>
                        {formatCurrency((client.linear_budget || 0) + (client.streaming_budget || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Modal Footer */}
          {stateClients.length > 0 && (
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                <strong style={{ color: '#111827' }}>{stateClients.length}</strong> client{stateClients.length !== 1 ? 's' : ''}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Total Budget: <strong style={{ color: '#111827', fontFamily: 'monospace' }}>{formatCurrency(getStateBudgetTotal(selectedState))}</strong>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0', color: 'var(--color-text-primary)', letterSpacing: '-0.02em' }}>
          2026 Primary Calendar
        </h1>
        <p style={{ color: 'var(--color-text-muted)', margin: '0 0 28px 0', fontSize: '14px' }}>
          State primary election dates and runoffs
        </p>
      </div>

      {/* Controls */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setViewMode('timeline')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: viewMode === 'timeline' ? 'var(--color-sidebar-bg)' : 'var(--color-surface-raised)',
              color: viewMode === 'timeline' ? 'white' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Timeline
          </button>
          <button
            onClick={() => setViewMode('list')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: viewMode === 'list' ? 'var(--color-sidebar-bg)' : 'var(--color-surface-raised)',
              color: viewMode === 'list' ? 'white' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            List
          </button>
          
         <button
            onClick={() => setViewMode('chart')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: viewMode === 'chart' ? 'var(--color-sidebar-bg)' : 'var(--color-surface-raised)',
              color: viewMode === 'chart' ? 'white' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📈 GMP Chart
          </button> 

        </div>
        
        <label style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#374151'
        }}>
          <input
            type="checkbox"
            checked={showOnlyClientStates}
            onChange={(e) => setShowOnlyClientStates(e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          Show only states with clients ({clientStates.length} states)
        </label>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          backgroundColor: '#fef2f2',
          borderRadius: 'var(--radius-lg)',
          padding: '20px',
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: 'var(--shadow-sm)',
          borderLeft: '4px solid #ef4444'
        }}>
          <div style={{ fontSize: '14px', color: '#991b1b', marginBottom: '4px' }}>Next 14 Days</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>
            {sortedPrimaries.filter(p => {
              const days = getDaysUntilPrimary(p.state)
              return days >= 0 && days <= 14
            }).length}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>primaries</div>
        </div>
        
        <div style={{
          backgroundColor: '#fffbeb',
          borderRadius: 'var(--radius-lg)',
          padding: '20px',
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: 'var(--shadow-sm)',
          borderLeft: '4px solid #f59e0b'
        }}>
          <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '4px' }}>Next 30 Days</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>
            {sortedPrimaries.filter(p => {
              const days = getDaysUntilPrimary(p.state)
              return days >= 0 && days <= 30
            }).length}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>primaries</div>
        </div>
        
        <div style={{
          backgroundColor: '#f0fdf4',
          borderRadius: 'var(--radius-lg)',
          padding: '20px',
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: 'var(--shadow-sm)',
          borderLeft: '4px solid #22c55e'
        }}>
          <div style={{ fontSize: '14px', color: '#166534', marginBottom: '4px' }}>States with Clients</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>
            {clientStates.length}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>of 50 states</div>
        </div>
        
        <div style={{
          backgroundColor: 'var(--color-surface-raised)',
          borderRadius: 'var(--radius-lg)',
          padding: '20px',
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: 'var(--shadow-sm)',
          borderLeft: '4px solid #64748b'
        }}>
          <div style={{ fontSize: '14px', color: '#475569', marginBottom: '4px' }}>Showing</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>
            {sortedPrimaries.length}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>primaries</div>
        </div>
      </div>

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div>
          {Object.entries(groupedByMonth).map(([month, primaries]) => (
            <div key={month} style={{ marginBottom: '32px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#111827',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e5e7eb'
              }}>
                {month}
              </h2>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '12px'
              }}>
                {primaries.map(p => (
                  <div 
                    key={p.state}
                    onClick={() => hasClients(p.state) && setSelectedState(p.state)}
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      borderRadius: 'var(--radius-md)',
                      padding: '16px',
                      boxShadow: 'var(--shadow-sm)',
                      border: '1px solid var(--color-border)',
                      borderLeft: `3px solid ${getUrgencyColor(p.state)}`,
                      opacity: getDaysUntilPrimary(p.state) < 0 ? 0.5 : 1,
                      cursor: hasClients(p.state) ? 'pointer' : 'default',
                      transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => {
                      if (hasClients(p.state)) {
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = 'var(--shadow-lg)'
                        e.currentTarget.style.borderColor = 'var(--color-accent)'
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-sm)'
                      e.currentTarget.style.borderColor = 'var(--color-border)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#111827',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {p.name}
                          {hasClients(p.state) && (
                            <span style={{
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '500'
                            }}>
                              {getClientCount(p.state)} client{getClientCount(p.state) !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                          {formatDate(p.primary)}
                        </div>
                        {p.runoff && (
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                            Runoff: {formatDate(p.runoff)}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: getUrgencyColor(p.state),
                        textAlign: 'right'
                      }}>
                        {getDaysLabel(p.state)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-raised)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>State</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Primary Date</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Runoff</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Days Until</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Clients</th>
              </tr>
            </thead>
            <tbody>
              {sortedPrimaries.map((p, idx) => (
                <tr 
                  key={p.state} 
                  onClick={() => hasClients(p.state) && setSelectedState(p.state)}
                  style={{ 
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb',
                    opacity: getDaysUntilPrimary(p.state) < 0 ? 0.5 : 1,
                    cursor: hasClients(p.state) ? 'pointer' : 'default'
                  }}
                  onMouseEnter={e => {
                    if (hasClients(p.state)) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6'
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'white' : '#f9fafb'
                  }}
                >
                  <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                    {p.name} ({p.state})
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>
                    {formatDate(p.primary)}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                    {p.runoff ? formatDate(p.runoff) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: getUrgencyColor(p.state)
                    }}>
                      {getDaysLabel(p.state)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {hasClients(p.state) ? (
                      <span style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>
                        {getClientCount(p.state)}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* GMP Chart View */}
      {viewMode === 'chart' && (
        <GMPTimeline clients={clients} />
      )}

      {/* Modal */}
      <StateModal />
    </div>
  )
}

export default PrimaryCalendar